import { newFuid } from '@utils';

/**
 * An ECMA identifier.
 */
const identifierSymbol = Symbol('Identifier');
export type Identifier =
  | string
  | (string & { readonly [identifierSymbol]: unique symbol });

/**
 * Generates a new identifier.
 *
 * @remarks
 * This function generates a unique identifier in some domain.
 *
 * - If no domain is specified, it will generate a globally unique random identifier.
 * - If a domain is specified, but no list of existing identifiers is provided, it will generate a unique identifier `domain_<random>`.
 * - If a domain and a list of existing identifiers are provided, it will iterate starting with 1 until it finds a unique identifier in the form of `domain_<number>`.
 * - If the identifier would start with a digit, it will be prefixed with a `$` sign to ensure it is a valid ECMA identifier.
 *
 * @returns A new identifier.
 */
export function newIdentifier(
  domain?: string,
  existingIds?: Iterable<Identifier>
): Identifier {
  let candidate: string;
  if (domain === undefined) {
    // no domain: fallback to random ID
    candidate = newFuid();
  } else if (existingIds === undefined) {
    // domain only: generate domain_<random>
    candidate = `${domain}${newFuid()}`;
  } else {
    // domain and existing IDs: iterate until finding a unique numbered ID
    const used = new Set<string>(existingIds as Iterable<string>);
    let index = 1;
    do {
      candidate = `${domain}${index++}`;
    } while (used.has(candidate));
  }
  // prefix with $ if it starts with a digit
  return (/^\d/.test(candidate) ? `$${candidate}` : candidate) as Identifier;
}

/**
 * Comparator for natural sorting of identifiers.
 *
 * If both identifiers start with letters and end with digits, they are compared
 * first by their letter prefix, then numerically by their digit suffix.
 * Identifiers matching this pattern are sorted before those that do not.
 * If neither matches, fallback to simple lexicographic comparison.
 */
export function compareIdentifiersNatural(a: Identifier, b: Identifier): number {
  const idA = typeof a === 'string' ? a : String(a);
  const idB = typeof b === 'string' ? b : String(b);
  const pattern = /^([A-Za-z]+)(\d+)$/;
  const matchA = pattern.exec(idA);
  const matchB = pattern.exec(idB);

  if (matchA && matchB) {
    const [_, prefixA, numAstr] = matchA;
    const [__, prefixB, numBstr] = matchB;
    if (prefixA < prefixB) return -1;
    if (prefixA > prefixB) return 1;
    const numA = parseInt(numAstr, 10);
    const numB = parseInt(numBstr, 10);
    return numA - numB;
  }

  if (matchA && !matchB) {
    return -1;
  }
  if (!matchA && matchB) {
    return 1;
  }

  return idA < idB ? -1 : idA > idB ? 1 : 0;
}
